import axios from 'axios';
import {
    IAudioData,
    IAudioDataStatistic,
    IAudioEmotionEvent,
} from '../types/AudioData';
import { ISessionAnalytics } from '../types/AnalyticsData';
import { IEngagementEmotion } from '../types/Config';

export interface IAudioService {
    fetchAudioData(userEmail: string, sessionName: string): Promise<IAudioData>;
    fetchAudioStatistic(
        userEmail: string,
        sessionName: string,
        fromSeconds?: number,
        toSeconds?: number,
    ): Promise<IAudioDataStatistic>;
    fetchSessionAnalytics(
        userEmail: string,
        sessionName: string,
    ): Promise<ISessionAnalytics>;
    fetchFmmData(
        userEmail: string,
        sessionName: string,
        levels?: number[],
        avrSeconds?: number,
        emotionsAndWeight?: IEngagementEmotion[],
    ): Promise<IAudioEmotionEvent[]>;
    fetchFmmSummary(
        userEmail: string,
        sessionName: string,
        interval?: {
            from: number;
            to: number;
        },
        levels?: number[],
        avrSeconds?: number,
        emotionsAndWeight?: IEngagementEmotion[],
    ): Promise<number[]>;
}

class AudioService implements IAudioService {
    async fetchAudioData(
        userEmail: string,
        sessionName: string,
    ): Promise<IAudioData> {
        const turnTakingData = await axios.get<IAudioData>(
            `/audioData/${userEmail}/${sessionName}`,
        );
        return turnTakingData.data;
    }

    async fetchAudioStatistic(
        userEmail: string,
        sessionName: string,
        fromSeconds?: number,
        toSeconds?: number,
    ): Promise<IAudioDataStatistic> {
        const audioStatisticData = await axios.get<IAudioDataStatistic>(
            `/audioStatistics/${userEmail}/${sessionName}${
                fromSeconds && toSeconds
                    ? `?from=${fromSeconds}&to=${toSeconds}`
                    : ''
            }`,
        );
        return audioStatisticData.data;
    }

    async fetchSessionAnalytics(
        userEmail: string,
        sessionName: string,
    ): Promise<ISessionAnalytics> {
        const sessionAnalyticsData = await axios.get<ISessionAnalytics>(
            `/session-analytics/${userEmail}/${sessionName}`,
        );
        return sessionAnalyticsData.data;
    }

    async fetchFmmData(
        userEmail: string,
        sessionName: string,
        levels?: number[],
        avrSeconds?: number,
        emotionsAndWeight?: IEngagementEmotion[],
    ): Promise<IAudioEmotionEvent[]> {
        const formattedLevels = levels
            ? JSON.stringify(levels.sort()).replace(/[\[\]']+/g, '')
            : undefined;
        const formattedEmotions = emotionsAndWeight?.reduce(
            (acc, cur) => ({ ...acc, [cur.id]: cur.value }),
            {},
        );

        const sessionFmmData = await axios.get<IAudioEmotionEvent[]>(
            `/fmmData/${userEmail}/${sessionName}`,
            {
                params: {
                    levels: formattedLevels,
                    avrSeconds,
                    ...formattedEmotions,
                },
            },
        );

        return sessionFmmData.data;
    }

    async fetchFmmEmotionsDefaultConfig(): Promise<{ [key: string]: number }> {
        const fieldsAndWeights = await axios.get<{ [key: string]: number }>(
            `/fmmData/defaultFieldWeights`,
        );

        return fieldsAndWeights.data;
    }

    async fetchFmmSummary(
        userEmail: string,
        sessionName: string,
        interval?: {
            from: number;
            to: number;
        },
        levels?: number[],
        avrSeconds?: number,
        emotionsAndWeight?: IEngagementEmotion[],
    ): Promise<number[]> {
        const formattedLevels = levels
            ? JSON.stringify(levels.sort()).replace(/[\[\]']+/g, '')
            : undefined;
        const formattedEmotions = emotionsAndWeight?.reduce(
            (acc, cur) => ({ ...acc, [cur.id]: cur.value }),
            {},
        );
        const sessionFmmData = await axios.get<number[]>(
            `/fmmStatistics/${userEmail}/${sessionName}`,
            {
                params: {
                    from: interval?.from,
                    to: interval?.to,
                    levels: formattedLevels,
                    avrSeconds,
                    ...formattedEmotions,
                },
            },
        );

        return sessionFmmData.data;
    }
}

const AudioServiceInstance = new AudioService();

export default AudioServiceInstance;
